export type Ga4Item = {
	item_id: string;
	item_name: string;
	item_brand?: string;
	price?: number;
	quantity?: number;
};

export type Ga4EcommercePayload = {
	currency?: string;
	value?: number;
	items: Ga4Item[];
};

type Ga4CartExtras = {
	cart_id?: string;
	cart_total_quantity?: number;
};

function pushGa4EcommerceEvent(
	event: string,
	ecommerce: Ga4EcommercePayload,
	extra?: Ga4CartExtras,
): void {
	if (typeof window === "undefined") return;

	const w = window as Window & { dataLayer?: Record<string, unknown>[] };
	w.dataLayer = w.dataLayer || [];
	w.dataLayer.push({ ecommerce: null });
	w.dataLayer.push({
		event,
		ecommerce,
		...extra,
	});

	if (
		process.env.NODE_ENV === "development" ||
		(w as Window & { __GA4_DEBUG__?: boolean }).__GA4_DEBUG__
	) {
		console.info("[GA4]", event, { ecommerce, ...extra });
	}
}

function safeTrack(fn: () => void): void {
	try {
		fn();
	} catch {
		// Swallow analytics errors silently.
	}
}

export function mapShopifyCartToGa4(cart: {
	id?: string;
	totalQuantity?: number;
	cost?: { subtotalAmount?: { amount: string; currencyCode: string } };
	lines?: {
		edges?: Array<{
			node?: {
				quantity?: number;
				merchandise?: {
					id?: string;
					product?: { title?: string };
					price?: { amount: string; currencyCode: string };
				};
			};
		}>;
	};
}): Ga4EcommercePayload & Ga4CartExtras {
	const currency = cart.cost?.subtotalAmount?.currencyCode ?? "EUR";
	const value = cart.cost?.subtotalAmount?.amount
		? Number.parseFloat(cart.cost.subtotalAmount.amount)
		: 0;

	const items: Ga4Item[] = (cart.lines?.edges ?? []).flatMap((edge) => {
		const node = edge.node;
		const merchandise = node?.merchandise;
		if (!merchandise?.id) return [];

		const item: Ga4Item = {
			item_id: merchandise.id,
			item_name: merchandise.product?.title ?? "",
			quantity: node?.quantity ?? 1,
		};
		if (merchandise.price?.amount) {
			item.price = Number.parseFloat(merchandise.price.amount);
		}
		return [item];
	});

	return {
		currency,
		value,
		items,
		cart_id: cart.id,
		cart_total_quantity: cart.totalQuantity,
	};
}

export function trackAddToCart(
	ecommerce: Ga4EcommercePayload,
	extra?: Ga4CartExtras,
): void {
	safeTrack(() => pushGa4EcommerceEvent("add_to_cart", ecommerce, extra));
}

export function trackViewItem(ecommerce: Ga4EcommercePayload): void {
	safeTrack(() => pushGa4EcommerceEvent("view_item", ecommerce));
}

export function trackViewCart(
	ecommerce: Ga4EcommercePayload,
	extra?: Ga4CartExtras,
): void {
	safeTrack(() => pushGa4EcommerceEvent("view_cart", ecommerce, extra));
}

export function trackBeginCheckout(
	ecommerce: Ga4EcommercePayload,
	extra?: Ga4CartExtras,
): void {
	safeTrack(() => pushGa4EcommerceEvent("begin_checkout", ecommerce, extra));
}
